/**
 * PromptField — campo de prompt compartido por todos los nodos.
 *
 * Reemplaza al `<textarea>` suelto que cada nodo repetía, agregando tres
 * cosas sin cambiar nada de la lógica del nodo (sigue siendo `value` +
 * `onChange`, el nodo persiste como siempre):
 *
 *   · MIC de dictado con transcripción en vivo (mismo hook que Lumière).
 *   · Botón para AMPLIAR el campo en un modal cómodo de leer y escribir.
 *   · En el modal: "Prompt Master" (mejora la redacción SIN inventar nada,
 *     con vista antes/después) y "Directors" (el pipeline cinematográfico
 *     que ya existía).
 *
 * El modal se abre con doble click en el campo o con el botón de ampliar:
 * escribir directo en el nodo sigue funcionando igual que antes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Maximize2, Mic, MicOff, Sparkles, X, Check, RotateCcw, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { promptMasterService } from '../../services/promptMasterService';
import DirectorsTrigger from './DirectorsTrigger';

const PromptField = ({
  value = '',
  onChange,
  nodeId,
  nodeType = 'generic',
  placeholder = '',
  rows = 2,
  className = 'node-prompt-input',
  testId,
  label = 'Prompt',
  enhance = true,          // muestra Prompt Master + Directors en el modal
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { original, improved, reason, credits_charged, uses_until_charge }
  const modalRef = useRef(null);

  // ── Dictado (mismo hook que el mic de Lumière) ────────
  const baseRef = useRef('');
  const {
    supported: micSupported, listening, transcript, error: micError,
    start: micStart, stop: micStop, reset: micReset,
  } = useSpeechRecognition({ lang: 'es-AR', interim: true });

  useEffect(() => {
    if (micError) toast.error(micError);
  }, [micError]);

  const lastAppliedRef = useRef('');
  useEffect(() => {
    if (!listening || !transcript) return;
    if (transcript === lastAppliedRef.current) return; // evita re-escribir lo mismo
    lastAppliedRef.current = transcript;
    const base = baseRef.current;
    const next = base ? `${base.replace(/\s+$/, '')} ${transcript}` : transcript;
    onChange?.(next);
  }, [transcript, listening, onChange]);

  const toggleMic = useCallback(() => {
    if (!micSupported) {
      toast.error('Tu navegador no soporta dictado por voz. Probá en Chrome o Edge.');
      return;
    }
    if (listening) {
      micStop();
      return;
    }
    baseRef.current = value || '';
    lastAppliedRef.current = '';
    micReset();
    micStart();
  }, [micSupported, listening, micStop, micReset, micStart, value]);

  // Al cerrar el modal se corta el dictado para no dejarlo grabando.
  const closeModal = useCallback(() => {
    if (listening) micStop();
    setSuggestion(null);
    setExpanded(false);
  }, [listening, micStop]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => modalRef.current?.focus(), 30);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [expanded, closeModal]);

  // ── Prompt Master ─────────────────────────────────────
  const runPromptMaster = async () => {
    const current = (value || '').trim();
    if (current.length < 3) {
      toast.error('Escribí al menos unas palabras para poder mejorarlas.');
      return;
    }
    setBusy(true);
    setSuggestion(null);
    try {
      const data = await promptMasterService.enhance({ prompt: current, nodeType });
      if (!data.changed) {
        toast.success(data.reason || 'Tu prompt ya estaba bien escrito.');
      } else {
        setSuggestion(data);
      }
      if (data.credits_charged) {
        toast.info(`Se cobró 1 crédito (1 cada ${data.charge_every} usos del Prompt Master).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'No se pudo mejorar el prompt');
    } finally {
      setBusy(false);
    }
  };

  const acceptSuggestion = () => {
    onChange?.(suggestion.improved);
    setSuggestion(null);
    toast.success('Prompt mejorado aplicado.');
  };

  const micButton = (size = 12) => (
    <button
      type="button"
      className={`promptfield-mic ${listening ? 'listening' : ''}`}
      onClick={toggleMic}
      disabled={disabled}
      title={listening ? 'Detener dictado' : 'Dictar el prompt por voz'}
      data-testid={`promptfield-mic-${nodeType}`}
    >
      {listening ? <MicOff size={size} /> : <Mic size={size} />}
    </button>
  );

  return (
    <>
      <div className="promptfield nodrag">
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onDoubleClick={() => setExpanded(true)}
          placeholder={placeholder}
          className={className}
          rows={rows}
          disabled={disabled}
          data-testid={testId}
          title="Doble click para ampliar"
        />
        <div className="promptfield-tools">
          {micButton(12)}
          <button
            type="button"
            className="promptfield-expand"
            onClick={() => setExpanded(true)}
            title="Ampliar el campo (doble click en el texto)"
            data-testid={`promptfield-expand-${nodeType}`}
          >
            <Maximize2 size={12} />
            <span>Ampliar</span>
          </button>
        </div>
        {listening && (
          <div className="promptfield-listening" data-testid="promptfield-listening">
            <span className="promptfield-dot" /> Escuchando…
          </div>
        )}
      </div>

      {expanded && createPortal(
        <div
          className="promptfield-backdrop"
          onClick={closeModal}
          data-testid="promptfield-modal-backdrop"
        >
          <div
            className="promptfield-modal nodrag nowheel"
            onClick={(e) => e.stopPropagation()}
            data-testid="promptfield-modal"
          >
            <header className="promptfield-modal-head">
              <div>
                <span className="promptfield-modal-kicker">{nodeType}</span>
                <h3>{label}</h3>
              </div>
              <button
                type="button"
                className="promptfield-close"
                onClick={closeModal}
                aria-label="Cerrar"
                data-testid="promptfield-close"
              >
                <X size={16} />
              </button>
            </header>

            <textarea
              ref={modalRef}
              className="promptfield-modal-textarea"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              data-testid="promptfield-modal-textarea"
            />

            <div className="promptfield-modal-meta">
              <span>{(value || '').length} caracteres</span>
              {listening && (
                <span className="promptfield-listening inline">
                  <span className="promptfield-dot" /> Escuchando… hablá normal, se transcribe solo
                </span>
              )}
            </div>

            {suggestion && (
              <div className="promptfield-diff" data-testid="promptfield-diff">
                <div className="promptfield-diff-col">
                  <span className="promptfield-diff-label">Tu prompt</span>
                  <p>{suggestion.original}</p>
                </div>
                <div className="promptfield-diff-col improved">
                  <span className="promptfield-diff-label">Mejorado</span>
                  <p>{suggestion.improved}</p>
                </div>
                <div className="promptfield-diff-actions">
                  <button
                    type="button"
                    className="promptfield-btn primary"
                    onClick={acceptSuggestion}
                    data-testid="promptfield-accept-improved"
                  >
                    <Check size={13} /> Usar mejorado
                  </button>
                  <button
                    type="button"
                    className="promptfield-btn"
                    onClick={() => setSuggestion(null)}
                    data-testid="promptfield-keep-mine"
                  >
                    <RotateCcw size={13} /> Mantener el mío
                  </button>
                </div>
              </div>
            )}

            <footer className="promptfield-modal-foot">
              <div className="promptfield-foot-left">
                {micButton(14)}
                {enhance && (
                  <>
                    <button
                      type="button"
                      className="promptfield-btn master"
                      onClick={runPromptMaster}
                      disabled={busy || disabled}
                      title="Mejora la redacción de tu prompt sin agregar nada que no escribiste"
                      data-testid="promptfield-master"
                    >
                      {busy ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                      {busy ? 'Mejorando…' : 'Prompt Master'}
                    </button>
                    <DirectorsTrigger
                      nodeId={nodeId}
                      nodeType={nodeType}
                      currentPrompt={value}
                      onInsert={(text) => onChange?.(text)}
                      className="in-promptfield"
                    />
                  </>
                )}
              </div>
              <button
                type="button"
                className="promptfield-btn primary"
                onClick={closeModal}
                data-testid="promptfield-done"
              >
                Listo
              </button>
            </footer>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default PromptField;
